import React, { useState, useEffect } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import FichaTecnicaModal from './FichaTecnicaModal';
import { useGlobalContext } from "../Config";

const TableProductos = (
    {
        header,
        productos,
        loading,
        globalFilterValue,
        tipoProductoOptions,
        handleEditProducto,
        nombreEmpresa,
    }
) => {

    const [showFichaModal, setShowFichaModal] = useState(false);
    const [selectedProductoFicha, setSelectedProductoFicha] = useState(null);

    const openFichaModal = (producto) => {
        setSelectedProductoFicha(producto);
        setShowFichaModal(true);
    };

    const closeFichaModal = () => {
        setShowFichaModal(false);
        setSelectedProductoFicha(null);
    };

    const { baseUrl } = useGlobalContext();
    const [localProductos, setLocalProductos] = useState(Array.isArray(productos) ? productos : []);

    useEffect(() => {
        setLocalProductos(Array.isArray(productos) ? productos : []);
    }, [productos]);

    const buildDeleteProductoSubproductoUrl = (idProductoSubproducto) => {
        return `${baseUrl}deleteProductoSubproducto?idProductoSubproducto=${idProductoSubproducto}`;
    };

    const handleDeleteProductoSubproducto = async (idSubproducto) => {
        try {
            const idProductoSubproducto = idSubproducto;

            const url = buildDeleteProductoSubproductoUrl(idProductoSubproducto);
            const res = await fetch(url, { method: "PUT" });
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || `Error al desasociar subproducto (HTTP ${res.status})`);
            }

            setLocalProductos(prev =>
                prev.map(p => {
                    if (p.id !== p.id) return p;
                    const lista = Array.isArray(p.subproductos?.lista) ? p.subproductos.lista : [];
                    const nuevaLista = lista.filter(s => s.id !== idSubproducto);
                    return {
                        ...p,
                        subproductos: {
                            ...(p.subproductos || {}),
                            lista: nuevaLista
                        }
                    };
                })
            );
        } catch (error) {
            console.error("Error desasociando subproducto:", error);
            alert(error.message || "Error al desasociar subproducto");
        }
    };

    return (
        <>
            <DataTable 
                header={header}
                value={localProductos}
                className="p-datatable-gridlines mt-3"
                showGridlines
                paginator
                rows={10}
                rowsPerPageOptions={[10, 20, 50, 100]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                currentPageReportTemplate={`Mostrando {first} a {last} de {totalRecords} productos ${nombreEmpresa}`}
                dataKey="id"
                loading={loading}
                emptyMessage="No se encontraron productos."
                stripedRows="true"
                id="productosTable"
                globalFilter={globalFilterValue}
                globalFilterFields={["descripcion"]}
                reorderableColumns
                resizableColumns
                columnResizeMode="fit"
                tableStyle={{
                    tableLayout: "fixed",
                    width: "100%",
                    minWidth: "1000px",
                }}
                scrollHeight="57vh"
                scrollable={true}
            >

                <Column 
                    field="descripcion" 
                    header="Descripción" 
                    sortable
                    style={{ width: '11%' }}
                    bodyStyle={{ overflow: 'hidden' }}
                    body={(rowData) => (
                        <span
                            style={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                            title={rowData.descripcion}
                        >
                            {rowData.descripcion}
                        </span>
                    )}
                />

                <Column
                    field="tarifa"
                    header="Tarifa"
                    sortable
                    body={(rowData) =>
                    new Intl.NumberFormat("es-CL", {
                        style: "decimal",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(rowData.tarifa)
                    }
                />

                <Column 
                    field="metraje" header="Metraje (m²)" sortable 
                />
                
                <Column 
                    field="tipoProducto" 
                    header="Tipo Producto" 
                    sortable 
                    filter 
                    filterElement={(options) => (
                        <Dropdown
                            value={options.value}
                            options={tipoProductoOptions.map(tipo => ({
                            label: tipo.descripcion,
                            value: tipo.descripcion
                            }))}
                            onChange={(e) => options.filterCallback(e.value)}
                            placeholder="Filtrar por tipo"
                            className="p-column-filter"
                            showClear
                        />
                    )}
                    filterMatchMode="equals"
                />

                <Column
                    field="subproductos"
                    header="Subproductos"
                    body={(rowData) => {
                        const lista = Array.isArray(rowData.subproductos?.lista) ? rowData.subproductos.lista : [];
                        if (lista.length === 0) return '-';
                        return (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {lista.map((a) => (
                                    <span
                                        key={`${rowData.id}-subp-${a.id}`}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.35rem',
                                            background: '#f4f4f4',
                                            borderRadius: '12px',
                                            padding: '2px 8px',
                                            maxWidth: '100%',
                                        }}
                                        title={a.descripcion}
                                    >
                                        <span style={{
                                            display: 'inline-block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '200px'
                                        }}>
                                            {a.descripcion}
                                        </span>
                                        <Button
                                            icon="pi pi-times"
                                            className="p-button-text p-button-danger p-button-sm"
                                            onClick={() => handleDeleteProductoSubproducto(a.id)}
                                            tooltip="Desasociar subproducto"
                                            tooltipOptions={{ position: 'top' }}
                                        />
                                    </span>
                                ))}
                            </div>
                        );
                    }}
                    sortable
                />

                <Column
                field="adyacentes"
                header="Productos Adyacentes"
                body={(rowData) => rowData.adyacentes 
                    ? rowData.adyacentes.lista.map(a => a.descripcion).join(', ') 
                    : '-'}
                sortable
                />

                <Column
                    field="activo"
                    header="Estados"
                    body={(rowData) => (
                    <>
                        <div>
                            {rowData.activo ? (
                            <Button
                                icon="pi pi-check"
                                className="p-button-rounded p-button-success p-button mr-2 mb-2 mt-2"
                                outlined
                                tooltip="Producto activo"
                                tooltipOptions={{ position: 'top' }}
                            />
                            ) : (
                            <Button
                                icon="pi pi-times"
                                className="p-button-rounded p-button-danger p-button mr-2 mb-2 mt-2"
                                onClick={() => {}}
                                outlined
                                tooltip="Producto inactivo"
                                tooltipOptions={{ position: 'top' }}
                            />
                            )}

                            {rowData.combo.combinado ? (
                            <Button
                                icon="pi pi-link"
                                className="p-button-rounded p-button-secondary p-button mr-2 mb-2 mt-2"
                                outlined
                                tooltip="Producto Combinado"
                                tooltipOptions={{ position: 'top' }}
                            />
                            ) : (
                            ''
                            )} 

                            {rowData.asignado.asignado ? (
                            <Button
                                icon="pi pi-calendar"
                                className="p-button-rounded p-button p-button mr-2 mb-2 mt-2"
                                outlined
                                tooltip={`Producto asignado en asignación N° ${rowData.asignado.id_cotizacion}`}
                                tooltipOptions={{ position: 'top' }}
                            />
                            ) : (
                            ''
                            )}

                        </div>
                    </>
                    )}
                    sortable
                />

                <Column
                    header="Acciones"
                    body={(rowData) => (
                    <>
                        <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-success p-button mr-2 mb-2 mt-2"
                        onClick={() => handleEditProducto(rowData)}
                        tooltip={rowData.asignado.asignado===true?"Producto asignado, no se puede editar":"Editar Producto"}
                        label="Editar"
                        outlined
                        disabled={rowData.asignado.asignado===true || rowData.activo===0}
                        />

                        <Button
                        icon="pi pi-file-o"
                        className="p-button-rounded p-button-info p-button-outlined p-button mr-2 mb-2 mt-2"
                        tooltip="Ver Ficha Técnica"
                        label="Ficha"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => openFichaModal(rowData)}
                        disabled={rowData.activo===0}
                        />

                    </>
                    )}
                />
            </DataTable>
            <FichaTecnicaModal
                visible={showFichaModal}
                onHide={closeFichaModal}
                productoId={selectedProductoFicha?.id}
                productoDescripcion={selectedProductoFicha?.descripcion}
            />
        </>
    );
};

export default TableProductos;
